import { useEffect } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { onBrowserTabs } from '../servers/commandStream'
import { BffError, getBrowserTabs, type HeldTab, type HeldTabsBrowser } from '../transport/relay'

export type HeldTabs = { browsers: HeldTabsBrowser[] }
export type Held = { browser: HeldTabsBrowser; told: HeldTab }

const HELD_TABS = ['browser-tabs'] as const

/**
 * A cloud from before this list has no such route: that is "no tabs", not an error to
 * show every person who opens More (the web and the cloud are deployed one at a time).
 */
async function readTabs(): Promise<HeldTabs> {
  try {
    return await getBrowserTabs()
  } catch (e) {
    if (e instanceof BffError && e.status === 404) return { browsers: [] }
    throw e
  }
}

/**
 * THE CHROME TABS AGENTS HOLD — the cloud's memory of what each connected browser last said.
 *
 * `live`: read again whenever the cloud says it changed (`browser_tabs`, at most once a
 * second) — More › Chrome tabs, a list someone opened to watch. The corner of a session
 * (AgentTabCorner) is not live: it is there whenever a session is, and a tab that retitles
 * on every message would have every open app read the list once a second (the review of
 * 2026-10-15). It reads the list when it is first shown and again at its own moments
 * (refreshHeldTabs); while More is open, both see More's reads.
 */
export function useHeldTabs({ live = true }: { live?: boolean } = {}) {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: HELD_TABS, queryFn: readTabs, staleTime: 0 })
  useEffect(() => (live ? listen(qc) : undefined), [qc, live])
  return list
}

/*
 * ONE LISTENER, however many watch: More and the sheet it opens are both live, and two
 * listeners were two reads of the list for every change — the second cancelling the first in
 * the app, but not the request it had already sent (the review of 2026-10-15, second round).
 */
const watching = new Map<QueryClient, { users: number; off: () => void }>()
function listen(qc: QueryClient): () => void {
  const w = watching.get(qc)
  if (w) w.users += 1
  else watching.set(qc, { users: 1, off: onBrowserTabs(() => void qc.invalidateQueries({ queryKey: HELD_TABS })) })
  return () => {
    const now = watching.get(qc)
    if (!now) return
    now.users -= 1
    if (now.users > 0) return
    now.off()
    watching.delete(qc)
  }
}

/** The list read now, for a moment that is about to choose from it. */
export const refreshHeldTabs = (qc: QueryClient) => qc.fetchQuery({ queryKey: HELD_TABS, queryFn: readTabs, staleTime: 0 })

/** The tabs one agent holds, with the browser each is in — the agent by its channel hash, on its machine. */
export function tabsHeldBy(browsers: readonly HeldTabsBrowser[], agent: { hash: string; device_id?: string | null }): Held[] {
  const out: Held[] = []
  for (const b of browsers)
    for (const t of b.tabs)
      // A machine's name for an agent is unique on that machine only; an account of one machine
      // may not have stamped its id on the row (AgentRow.device_id), and then there is one.
      if (t.agent.hash === agent.hash && (!agent.device_id || t.agent.device === agent.device_id)) out.push({ browser: b, told: t })
  return out
}

/** The agent a tab is given to, as the browser names it: its channel hash on the machine it is on. */
export type TabAgent = HeldTab['agent']
export type ByAgent = { key: string; agent: TabAgent; held: Held[] }

/**
 * The tabs, one group per agent that holds any (More › Chrome tabs — owner, 2026-10-15: 以 Agent
 * 名稱作為樹狀圖的第一層 … 使用者更明白該 Agent 掌控哪些分頁). In name order, then machine;
 * within a group, in the order the browsers listed them.
 */
export function byAgent(browsers: readonly HeldTabsBrowser[]): ByAgent[] {
  const groups = new Map<string, ByAgent>()
  for (const b of browsers)
    for (const t of b.tabs) {
      const key = `${t.agent.hash}|${t.agent.device}`
      const g = groups.get(key)
      if (g) g.held.push({ browser: b, told: t })
      else groups.set(key, { key, agent: t.agent, held: [{ browser: b, told: t }] })
    }
  return [...groups.values()].sort((x, y) => x.agent.session.localeCompare(y.agent.session) || x.agent.machine.localeCompare(y.agent.machine))
}

/** A tab by its browser and label, whoever holds it now — the way More's sheet follows its tab. */
export function findHeld(browsers: readonly HeldTabsBrowser[], browserId: string, tab: string): Held | null {
  const b = browsers.find((x) => x.browser_id === browserId)
  const t = b?.tabs.find((x) => x.tab === tab)
  return b && t ? { browser: b, told: t } : null
}
